import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronUp, HelpCircle, KeyRound, LogOut, MessageCircleQuestion, Moon, Route, Settings, Sun } from 'lucide-react'
import { cn } from '@/utils'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  Badge,
  Tip,
} from '@/components/ui'
import { useAuthStore } from '@/stores/authStore'
import { useAppStore } from '@/stores'
import { useAuth } from '@/hooks/useAuth'
import { useHelpFeedback } from '@/components/helpFeedback/helpFeedbackContext'
import { usePageTour } from '@/hooks/usePageTour'
import { TOUR_THIS_PAGE } from '@/components/tour/tourText'
import { useTourStore } from '@/stores/tourStore'
import type { PageTour } from '@/services/tour/tourModel'
import { ENTRY_LABEL } from '@/components/helpFeedback/helpFeedbackText'
import { HELP_SHORTCUT } from '@/components/helpFeedback/shortcut'

function getInitials(displayName: string, email: string): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return parts[0][0].toUpperCase()
  }
  return email[0].toUpperCase()
}

interface UserMenuProps {
  /** The collapsed rail: the avatar alone, opening to the right. */
  collapsed?: boolean
  /**
   * Where it sits: the sidebar foot (the default), or the phone's top bar
   * (SA-965), where it is the avatar alone on the page's tokens, opening
   * below and to the left.
   */
  variant?: 'sidebar' | 'bar'
  /** Called after an entry that leaves the page or opens Help & feedback; the sidebar closes the phone drawer. */
  onNavigate?: () => void
}

/**
 * Everything about the person, at the sidebar foot (SA-962, spec §3): their
 * settings, Help, Help & feedback, the theme, the password and logging out. Below
 * `lg` the top bar carries it too (SA-965).
 */
export function UserMenu({ collapsed = false, variant = 'sidebar', onNavigate }: UserMenuProps) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { logout } = useAuth()
  const theme = useAppStore((s) => s.theme)
  const setTheme = useAppStore((s) => s.setTheme)

  // The shell's one Help & feedback panel (SA-1306): below lg there are two
  // of this menu, and both open the same conversation.
  const helpFeedback = useHelpFeedback()
  // SA-1315: the page's tour, started once the menu has closed and given the focus back to its
  // trigger, so the tour's card takes it from there and returns it there.
  const pageTour = usePageTour()
  const pendingTour = useRef<PageTour | null>(null)
  // SA-1418 (B1): the avatar-only trigger's tip stays shut while the menu is open and on the focus
  // the menu's close hands back to the trigger; the person's next hover, blur or pointer move over
  // it lets the tip open again.
  const [menuOpen, setMenuOpen] = useState(false)
  const [tipOpen, setTipOpen] = useState(false)
  const suppressTip = useRef(false)
  const releaseTip = () => { suppressTip.current = false }
  const onMenuOpenChange = (open: boolean) => {
    setMenuOpen(open)
    setTipOpen(false)
    if (!open) suppressTip.current = true
  }
  const onTipOpenChange = (open: boolean) => {
    if (open && (menuOpen || suppressTip.current)) return
    setTipOpen(open)
  }

  if (!user) return null

  const initials = getInitials(user.display_name, user.email)

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  // Moved verbatim from Header.tsx (spec §3.4): the store write plus the class.
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)

    // Apply theme to document
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  const handleLogout = async () => {
    await logout()
    go('/login')
  }

  const avatar = (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center',
        'w-8 h-8 rounded-full',
        'bg-primary text-primary-foreground',
        'text-sm font-medium',
        'select-none'
      )}
    >
      {initials}
    </span>
  )

  const bar = variant === 'bar'
  const avatarOnly = bar || collapsed

  // SA-1034 PR 5 (spec §3.1): the avatar-only trigger says what it is as a
  // tip. The tooltip wraps the dropdown trigger, never the reverse: `Tip` is
  // a function component and cannot take `DropdownMenuTrigger`'s ref.
  const trigger = (
    <DropdownMenuTrigger asChild>
        <button
          className={
            bar
              ? cn(
                  'flex items-center rounded-full p-0.5',
                  'hover:bg-accent transition-colors',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring'
                )
              : // overflow-hidden: while the rail widens from 64px the row
                // is narrower than its avatar and name for about 125ms; the
                // trigger clips them, never its own focus ring (SA-983).
                cn(
                  'flex w-full items-center gap-2 overflow-hidden rounded-md p-1.5',
                  'hover:bg-sidebar-accent transition-colors',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                  collapsed && 'justify-center'
                )
          }
          aria-label="User menu"
          onBlur={releaseTip}
          onPointerMove={releaseTip}
          onPointerLeave={releaseTip}
        >
          {avatar}
          {!avatarOnly && (
            <>
              <span className="flex-1 min-w-0 truncate text-left text-sm font-medium text-sidebar-foreground">
                {user.display_name}
              </span>
              <ChevronUp className="w-4 h-4 shrink-0 text-sidebar-foreground/70" />
            </>
          )}
        </button>
    </DropdownMenuTrigger>
  )

  return (
    <DropdownMenu open={menuOpen} onOpenChange={onMenuOpenChange}>
      {avatarOnly
        ? <Tip text="User menu" side={bar ? 'bottom' : 'right'} open={!menuOpen && tipOpen} onOpenChange={onTipOpenChange}>{trigger}</Tip>
        : trigger}

      <DropdownMenuContent
        side={bar ? 'bottom' : collapsed ? 'right' : 'top'}
        align={bar || collapsed ? 'end' : 'start'}
        sideOffset={8}
        className="w-64"
        onCloseAutoFocus={() => {
          const tour = pendingTour.current
          pendingTour.current = null
          if (tour) queueMicrotask(() => useTourStore.getState().start(tour, 'menu'))
        }}
      >
        <DropdownMenuLabel className="flex flex-col gap-1 p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {user.display_name}
            </span>
            {(user.roles?.length ? user.roles : [user.role]).map((roleName) => (
              <Badge
                key={roleName}
                variant={roleName === 'admin' ? 'warning' : 'secondary'}
                className="text-[10px] px-1.5 py-0"
              >
                {roleName}
              </Badge>
            ))}
          </div>
          <span className="text-xs font-normal text-muted-foreground truncate">
            {user.email}
          </span>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => go('/settings')}>
          <Settings className="w-4 h-4 text-muted-foreground" />
          <span>Your settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/help')}>
          <HelpCircle className="w-4 h-4 text-muted-foreground" />
          <span>Help &amp; docs</span>
        </DropdownMenuItem>
        {pageTour && (
          <DropdownMenuItem
            onSelect={() => {
              pendingTour.current = pageTour
              onNavigate?.()
            }}
          >
            <Route className="w-4 h-4 text-muted-foreground" />
            <span>{TOUR_THIS_PAGE}</span>
          </DropdownMenuItem>
        )}
        {helpFeedback.available && (
          <DropdownMenuItem
            aria-keyshortcuts={HELP_SHORTCUT}
            onSelect={() => {
              helpFeedback.open()
              onNavigate?.()
            }}
          >
            <MessageCircleQuestion className="w-4 h-4 text-muted-foreground" />
            <span>{ENTRY_LABEL}</span>
            <DropdownMenuShortcut aria-hidden="true">{HELP_SHORTCUT}</DropdownMenuShortcut>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Names the theme it switches to, as the header's icon did. */}
        <DropdownMenuItem onSelect={toggleTheme}>
          {theme === 'dark' ? (
            <Sun className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Moon className="w-4 h-4 text-muted-foreground" />
          )}
          <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => go('/change-password')}>
          <KeyRound className="w-4 h-4 text-muted-foreground" />
          <span>Change password</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={handleLogout}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="w-4 h-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
